import React from 'react'
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Link,
  Button,
  LinearProgress,
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  elGR,
  enUS,
  getGridStringOperators,
  GridFilterModel,
  GridSortModel,
  GridValueGetterParams,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid'
import { ManageAccounts } from '@mui/icons-material'
import {
  Link as RouterLink,
  Outlet,
  LinkProps,
  useSearchParams,
} from 'react-router-dom'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import NoResultsOverlay from 'shared/table/overlays/NoResults'
import UserService from 'api/services/User'
import CustomPagination from 'shared/table/Pagination'
import { User } from 'types'
import usePageView from 'hooks/usePageView'

const LinkButton = styled(Button)<LinkProps>(({ theme }) => ({}))

const Users: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { sendPageView } = usePageView({ runOnMount: false })

  const IS_LOCALE_GR = i18n.language === 'gr'

  const { enqueueSnackbar } = useSnackbar()

  const [searchParams] = useSearchParams()
  const HAS_PARAM = searchParams.get('page')

  const [page, setPage] = React.useState(() =>
    HAS_PARAM ? Number(searchParams.get('page')) - 1 : 0
  )

  const [pageSize] = React.useState(10)
  const [results, setResults] = React.useState([] as User[])
  const [rowCountState, setRowCountState] = React.useState(0)

  const [filterValue, setFilterValue] = React.useState<object | undefined>()

  const [sortValue, setSortValue] = React.useState<object | undefined>()

  const onFilterChange = React.useCallback((filterModel: GridFilterModel) => {
    if (filterModel?.items?.length) {
      setSortValue({})
      const filter = filterModel?.items[0]
      let searchKey = filter?.columnField
      let searchValue = filterModel.items[0].value
      switch (filter?.columnField) {
        case 'companyName':
          searchKey = 'companyName'

          break
        case 'username':
          searchKey = 'userName'

          break
        case 'role':
          searchKey = 'roles'
          searchValue = [filterModel.items[0].value]
          break
      }
      setFilterValue({
        [searchKey]: searchValue,
      })
      setPage(0)
    } else {
      setSortValue({})
    }
  }, [])

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel.length) {
        setFilterValue({})
        const model: { sortingField?: string; sortingOrder?: string } = {}
        switch (sortModel[0].field) {
          case 'source':
            model.sortingField = sortModel[0]?.field?.toUpperCase()
            model.sortingOrder = sortModel[0]?.sort?.toUpperCase()
            break
          case 'companyName':
            model.sortingField = 'COMPANY_NAME'
            model.sortingOrder = sortModel[0]?.sort?.toUpperCase()
            break
          default:
            break
        }
        setSortValue(model)
        setPage(0)
      } else {
        setSortValue({})
      }
    },
    []
  )

  const countUsers = UserService.hooks.useCountUsers(filterValue)

  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      countUsers?.data !== undefined
        ? (countUsers?.data as number)
        : prevRowCountState
    )
  }, [countUsers.data, setRowCountState])

  const users = UserService.hooks.useUsers(
    page,
    pageSize,
    filterValue,
    sortValue,
    {
      enabled: countUsers.isSuccess && !!countUsers.data,
      onError: () => {
        enqueueSnackbar(t('tables.responses.error') as unknown as string, {
          variant: 'error',
        })
      },
      onSettled: () => {
        sendPageView()
      },
    }
  )

  React.useEffect(() => {
    setResults((prevState) => (users?.data !== undefined ? users.data : []))
  }, [users.data, setResults])

  const handlePageChange = (newPage) => {
    setPage(newPage)
  }

  const STATUSES = [
    {
      value: 'ACTIVE',
      label: 'tables.users.status.active',
    },
    {
      value: 'INACTIVE',
      label: 'tables.users.status.inactive',
    },
  ]

  const ROLES = [
    {
      value: 'USER',
      label: 'USER',
    },
    {
      value: 'ADMIN',
      label: 'ADMIN',
    },
    {
      value: 'ESTATE_AGENT',
      label: 'ESTATE_AGENT',
    },
  ]

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: t('tables.users.columns.firstName'),
      flex: 1,
      minWidth: 250,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      filterable: false,
      valueGetter: (params: GridValueGetterParams) => {
        return params.row?.person?.firstName ?? '-'
      },
    },
    {
      field: 'lastName',
      headerName: t('tables.users.columns.lastName'),
      flex: 1,
      minWidth: 250,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      filterable: false,
      valueGetter: (params: GridValueGetterParams) => {
        return params.row?.person?.lastName ?? '-'
      },
    },
    {
      field: 'username',
      flex: 1,
      headerName: 'Email',
      minWidth: 250,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === 'contains'
      ),
    },
    {
      field: 'role',
      flex: 1,
      headerName: 'Ρόλος',
      minWidth: 250,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      type: 'singleSelect',
      valueOptions: ROLES.map((item) => ({
        value: item.value,
        label: item.label,
      })),
      filterOperators: getGridSingleSelectOperators().filter(
        (operator) => operator.value === 'is'
      ),
      valueGetter: (params: GridValueGetterParams) => {
        return params.row?.roles?.[0]?.name
      },
    },
    {
      field: 'status',
      flex: 1,
      headerName: t('tables.users.columns.status'),
      minWidth: 150,
      headerClassName: 'table--header',
      type: 'singleSelect',
      valueOptions: STATUSES.map((item) => ({
        value: item.value,
        label: t(item.label),
      })),
      hideable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              bgcolor:
                params.row.entityStatus === 'ACTIVE'
                  ? 'success.main'
                  : 'error.main',
              color: 'common.white',
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            {params.row.entityStatus === 'ACTIVE'
              ? t('tables.users.status.active')
              : t('tables.users.status.inactive')}
          </Box>
        )
      },
    },

    {
      field: 'actions',
      flex: 1,
      headerClassName: 'table--header',
      type: 'actions',
      minWidth: 100,
      hideable: false,
      filterable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Stack direction="row" spacing={1}>
            <Tooltip
              title={
                t('tables.users.columns.actions.edit') as unknown as string
              }
            >
              <Link
                component={RouterLink}
                to={`/users/${params.row.id}`}
                underline="none"
              >
                <IconButton>
                  <ManageAccounts />
                </IconButton>
              </Link>
            </Tooltip>
          </Stack>
        )
      },
    },
  ]

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        px: { xs: 2, sm: 2, md: 3, lg: 4, xl: 6 },
        pt: 1,
        pb: 4,
        borderRadius: 2.5,
        mx: 'auto',
      }}
    >
      <Typography variant="h6" align="center">
        {t('tables.users.title')}
      </Typography>
      <Stack>
        <Stack
          direction="row"
          sx={{ alignSelf: 'flex-end', my: 2 }}
          spacing={1}
        >
          <LinkButton
            variant="contained"
            LinkComponent={RouterLink}
            to="new"
            sx={{ textTransform: 'none' }}
          >
            {t('tables.users.addButton')}
          </LinkButton>
        </Stack>
        <Box
          sx={{
            width: '100%',
            mt: 2,
          }}
        >
          <div style={{ display: 'flex', height: '100%' }}>
            <Box
              sx={{
                flexGrow: 1,
                '& .table--header': {
                  color: 'textPrimary',
                  fontSize: '1rem',
                  fontWeight: 600,
                },
              }}
            >
              <DataGrid
                localeText={
                  IS_LOCALE_GR
                    ? elGR.components.MuiDataGrid.defaultProps.localeText
                    : enUS.components.MuiDataGrid.defaultProps.localeText
                }
                autoHeight
                disableSelectionOnClick
                disableColumnSelector={true}
                rows={results}
                rowCount={rowCountState}
                columns={columns}
                page={page}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                rowsPerPageOptions={[10]}
                components={{
                  LoadingOverlay: LinearProgress,
                  NoRowsOverlay: NoResultsOverlay,
                  Pagination: CustomPagination,
                }}
                loading={users.isLoading || users.isFetching}
                paginationMode="server"
                filterMode="server"
                sortingMode="server"
                onFilterModelChange={onFilterChange}
                onSortModelChange={handleSortModelChange}
              />
            </Box>
          </div>
        </Box>
        <Outlet />
      </Stack>
    </Box>
  )
}

export default Users
